import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fetchEventById, selectCurrentEvent, selectEventsStatus } from '../data/eventSlice';
import { registerForEvent, selectRegisterLoading, selectRegistrationSuccess, selectRegistrationError, clearRegistrationSuccess, clearRegistrationError } from '../data/registrationSlice';
import { AuthService, baseURL } from '../services/APIService';
import { useBackendTranslation } from '../utils/useBackendTranslation';

const EventDetail = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const event = useSelector(selectCurrentEvent);
  const status = useSelector(selectEventsStatus);
  const registerLoading = useSelector(selectRegisterLoading);
  const registrationSuccess = useSelector(selectRegistrationSuccess);
  const registrationError = useSelector(selectRegistrationError);
  const isAuthenticated = AuthService.isAuthenticated();
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [notes, setNotes] = useState('');
  
  // Use the custom hook to translate event data
  const { translatedData: translatedEvent, isLoading: translationLoading } = useBackendTranslation(
    event, 
    { skipFields: ['id', 'date', 'time', 'imageData', 'hasImage', 'created_at', 'updated_at'] }
  );

  useEffect(() => {
    if (id) {
      dispatch(fetchEventById(id));
    }
  }, [dispatch, id]);
  
  useEffect(() => {
    // Clear success/error messages when component unmounts
    return () => {
      dispatch(clearRegistrationSuccess());
      dispatch(clearRegistrationError());
    };
  }, [dispatch]);
  
  useEffect(() => {
    if (registrationSuccess) {
      // Close the modal and clear form if registration is successful
      setShowRegistrationModal(false);
      setNotes('');
      
      // Clear success message after 5 seconds
      const timer = setTimeout(() => {
        dispatch(clearRegistrationSuccess());
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [registrationSuccess, dispatch]);

  // Format date to readable format
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const openRegistrationModal = () => {
    if (isAuthenticated && event) {
      setShowRegistrationModal(true);
    }
  };
  
  const closeRegistrationModal = () => {
    setShowRegistrationModal(false);
    setNotes('');
    dispatch(clearRegistrationError());
  };
  
  const handleRegister = (e) => {
    e.preventDefault();
    if (isAuthenticated && event) {
      dispatch(registerForEvent({ eventId: event.id, notes }));
    }
  };

  if (status === 'loading') {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h2 className="text-2xl font-bold text-gray-700">{t('event_not_found')}</h2>
        <p className="mt-4 text-gray-600">{t('event_not_found_description')}</p>
        <Link 
          to="/events" 
          className="inline-block mt-6 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-6 rounded-md"
        >
          {t('back_to_events')}
        </Link>
      </div>
    );
  }
  
  // Use the translated event data instead of the original
  const displayEvent = translationLoading ? event : translatedEvent;
  
  // Guard against null or undefined displayEvent
  if (!displayEvent) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-500 mx-auto mb-4"></div>
        <h2 className="text-2xl font-bold text-gray-700">{t('loading_event')}</h2>
      </div>
    );
  }

  return (
    <div className="bg-gray-50">
      {/* Hero Section */}
      <div className="relative h-[40vh] md:h-[50vh] lg:h-[60vh]">
        <img 
          src={displayEvent?.imageData ? displayEvent.imageData : (displayEvent?.hasImage ? `${baseURL}/events/image/${displayEvent.id}` : '/assets/image1.jpeg')}
          alt={displayEvent?.title || 'Event image'} 
          className="w-full h-full object-cover" 
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent"></div>
        
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 lg:p-16 text-white">
          <div className="container mx-auto">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <div className="inline-flex items-center bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-4">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {formatDate(displayEvent.date)}
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold">{displayEvent.title}</h1>
                <div className="flex items-center mt-4">
                  <div className="flex items-center mr-6">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>{displayEvent.location}</span>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 md:mt-0">
                {isAuthenticated ? (
                  <button 
                    onClick={openRegistrationModal}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 px-8 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl flex items-center"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                    {t('register_for_event')}
                  </button>
                ) : (
                  <Link 
                    to="/login" 
                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 px-8 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl flex items-center"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                    </svg>
                    {t('login_to_register')}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Event Details */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white shadow-lg rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('event_details')}</h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700">{displayEvent.description}</p>
            </div>
            
            <div className="mt-8 pt-8 border-t border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('event_information')}</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start">
                  <div className="bg-indigo-100 p-3 rounded-full mr-4">
                    <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">{t('date')}</h4>
                    <p className="text-base font-medium text-gray-900">{formatDate(displayEvent.date)}</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-indigo-100 p-3 rounded-full mr-4">
                    <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">{t('location')}</h4>
                    <p className="text-base font-medium text-gray-900">{displayEvent.location}</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-8 flex flex-col sm:flex-row justify-between">
              <Link 
                to="/events" 
                className="flex items-center text-indigo-600 hover:text-indigo-800 transition-colors duration-300"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                {t('back_to_events')}
              </Link>
              
              {isAuthenticated ? (
                <button 
                  onClick={openRegistrationModal}
                  className="mt-4 sm:mt-0 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-6 rounded-md transition-all duration-300"
                >
                  {t('register_for_event')}
                </button>
              ) : (
                <Link 
                  to="/login" 
                  className="mt-4 sm:mt-0 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-6 rounded-md transition-all duration-300"
                >
                  {t('login_to_register')}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Registration Modal */}
      {showRegistrationModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            {/* Modal */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div 
              className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
              role="dialog" 
              aria-modal="true" 
              aria-labelledby="modal-headline"
            >
              <form onSubmit={handleRegister}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4" id="modal-headline">
                    {t('register_for_event')}
                  </h3>
                  
                  <div className="mb-4">
                    <label htmlFor="eventTitle" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('event')}
                    </label>
                    <p className="text-base text-gray-900">{displayEvent.title}</p>
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="eventDate" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('date')}
                    </label>
                    <p className="text-base text-gray-900">{formatDate(displayEvent.date)}</p>
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('special_notes')} ({t('optional')})
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      rows="3"
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      placeholder={t('notes_placeholder')}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      {t('notes_help')}
                    </p>
                  </div>
                  
                  {/* Error message */}
                  {registrationError && (
                    <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-red-700">{registrationError}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={registerLoading}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-70"
                  >
                    {registerLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('processing')}
                      </>
                    ) : t('submit_registration')}
                  </button>
                  <button
                    type="button"
                    onClick={closeRegistrationModal}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {t('cancel')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
      {/* Success message toast */}
      {registrationSuccess && (
        <div className="fixed bottom-4 right-4 z-50 bg-green-50 border-l-4 border-green-400 p-4 rounded-md shadow-lg max-w-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">{registrationSuccess}</p>
              <div className="mt-2">
                <Link 
                  to="/my-registrations" 
                  className="text-sm font-medium text-green-700 hover:text-green-600 underline"
                >
                  View my registrations
                </Link>
              </div>
            </div>
            <div className="ml-auto pl-3">
              <div className="-mx-1.5 -my-1.5">
                <button
                  onClick={() => dispatch(clearRegistrationSuccess())}
                  className="inline-flex rounded-md p-1.5 text-green-500 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <span className="sr-only">{t('dismiss')}</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetail;
